import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { Download, TrendingUp, Users, Package, Calendar as CalendarIcon, FileText, BarChart3, CheckCircle, Activity, Clock } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format, subDays, startOfMonth, endOfMonth, eachDayOfInterval, parseISO, isWithinInterval } from 'date-fns';
import { cn } from '@/lib/utils';
import { orderAPI, attendanceAPI, todoAPI, stockAPI, customerAPI, employeeAPI, activityLogAPI } from '@/lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const Reports = ({ user }) => {
  // Check if user is admin
  if (user?.role !== 'admin') {
    return (
      <Card className="max-w-2xl mx-auto mt-20">
        <CardContent className="p-12 text-center">
          <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
          <p className="text-muted-foreground">You don't have permission to access reports.</p>
        </CardContent>
      </Card>
    );
  }

  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState(30);
  const [startDate, setStartDate] = useState(subDays(new Date(), 30));
  const [endDate, setEndDate] = useState(new Date());
  
  const [reportData, setReportData] = useState({
    orders: [],
    attendance: [],
    todos: [],
    stocks: [],
    customers: [],
    employees: []
  });

  const [activityLogs, setActivityLogs] = useState([]);
  const [activityFilter, setActivityFilter] = useState({
    entity_type: '',
    username: ''
  });

  const [statistics, setStatistics] = useState({
    totalOrders: 0,
    ordersByStatus: {},
    totalCustomers: 0,
    totalEmployees: 0,
    avgAttendance: 0,
    totalTodos: 0,
    completedTodos: 0,
    lowStockItems: 0,
    totalStockItems: 0
  });

  useEffect(() => {
    loadReportData();
    loadActivityLogs();
  }, [dateRange]);

  const loadActivityLogs = async () => {
    try {
      const logs = await activityLogAPI.getAll({
        start_date: format(startDate, 'yyyy-MM-dd'),
        end_date: format(endDate, 'yyyy-MM-dd'),
        limit: 200,
        ...activityFilter
      });
      setActivityLogs(logs);
    } catch (error) {
      console.error('Error loading activity logs:', error);
    }
  };

  const loadReportData = async () => {
    setIsLoading(true);
    try {
      const [orders, todos, stocks, customers, employees] = await Promise.all([
        orderAPI.getAll().catch(() => []),
        todoAPI.getAll().catch(() => []),
        stockAPI.getAll().catch(() => []),
        customerAPI.getAll().catch(() => []),
        employeeAPI.getAll().catch(() => [])
      ]);

      // Load attendance for date range
      const attendancePromises = [];
      const days = eachDayOfInterval({ start: startDate, end: endDate });
      for (const day of days) {
        attendancePromises.push(
          attendanceAPI.getAll(format(day, 'yyyy-MM-dd')).catch(() => [])
        );
      }
      const attendanceByDay = await Promise.all(attendancePromises);
      const allAttendance = attendanceByDay.flat();

      setReportData({
        orders,
        attendance: allAttendance,
        todos,
        stocks,
        customers,
        employees
      });

      // Calculate statistics
      const ordersByStatus = orders.reduce((acc, order) => {
        acc[order.status] = (acc[order.status] || 0) + 1;
        return acc;
      }, {});

      const completedTodos = todos.filter(t => t.status === 'Completed').length;
      const lowStockItems = stocks.filter(s => s.reorder_point && s.quantity <= s.reorder_point).length;
      
      // Calculate average attendance
      const attendanceByDate = allAttendance.reduce((acc, att) => {
        if (!acc[att.date]) acc[att.date] = { present: 0, total: 0 };
        acc[att.date].total++;
        if (att.status === 'Present') acc[att.date].present++;
        return acc;
      }, {});
      
      const dates = Object.keys(attendanceByDate);
      const avgAttendance = dates.length > 0
        ? Math.round((dates.reduce((sum, date) => sum + attendanceByDate[date].present, 0) / dates.length))
        : 0;

      setStatistics({
        totalOrders: orders.length,
        ordersByStatus,
        totalCustomers: customers.length,
        totalEmployees: employees.length,
        avgAttendance,
        totalTodos: todos.length,
        completedTodos,
        lowStockItems,
        totalStockItems: stocks.length
      });

    } catch (error) {
      console.error('Error loading report data:', error);
      toast.error('Failed to load report data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateRangeChange = (days) => {
    setDateRange(days);
    setStartDate(subDays(new Date(), days));
    setEndDate(new Date());
  };

  const handleExport = (reportType) => {
    try {
      let csvContent = '';
      let filename = '';

      switch (reportType) {
        case 'orders':
          csvContent = 'Customer Name,Item Description,Type,Status,PO Number,Order Date\n';
          reportData.orders.forEach(order => {
            csvContent += `"${order.customer_name}","${order.item_description}","${order.order_type}","${order.status}","${order.po_number || ''}","${order.order_date || ''}"\n`;
          });
          filename = 'orders_report.csv';
          break;

        case 'attendance':
          csvContent = 'Employee Name,Date,Status,Check In,Check Out\n';
          reportData.attendance.forEach(att => {
            csvContent += `"${att.employee_name}","${att.date}","${att.status}","${att.check_in || ''}","${att.check_out || ''}"\n`;
          });
          filename = 'attendance_report.csv';
          break;

        case 'inventory':
          csvContent = 'Item Name,Category,Quantity,Unit,Reorder Point,Supplier\n';
          reportData.stocks.forEach(stock => {
            csvContent += `"${stock.item_name}","${stock.category}","${stock.quantity}","${stock.unit}","${stock.reorder_point || ''}","${stock.supplier || ''}"\n`;
          });
          filename = 'inventory_report.csv';
          break;

        case 'todos':
          csvContent = 'Title,Description,Priority,Status,Due Date\n';
          reportData.todos.forEach(todo => {
            csvContent += `"${todo.title}","${todo.description || ''}","${todo.priority}","${todo.status}","${todo.due_date || ''}"\n`;
          });
          filename = 'tasks_report.csv';
          break;

        case 'summary':
          csvContent = 'Metric,Value\n';
          csvContent += `Total Orders,${statistics.totalOrders}\n`;
          csvContent += `Total Customers,${statistics.totalCustomers}\n`;
          csvContent += `Total Employees,${statistics.totalEmployees}\n`;
          csvContent += `Average Attendance,${statistics.avgAttendance}\n`;
          csvContent += `Total Tasks,${statistics.totalTodos}\n`;
          csvContent += `Completed Tasks,${statistics.completedTodos}\n`;
          csvContent += `Low Stock Items,${statistics.lowStockItems}\n`;
          filename = 'summary_report.csv';
          break;
      }

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`${reportType} report exported successfully!`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export report');
    }
  };

  // Prepare chart data
  const orderStatusData = Object.entries(statistics.ordersByStatus).map(([status, count]) => ({
    name: status,
    value: count
  }));

  const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444'];

  const todoCompletionData = [
    { name: 'Completed', value: statistics.completedTodos },
    { name: 'Pending', value: statistics.totalTodos - statistics.completedTodos }
  ];

  const stockStatusData = [
    { name: 'In Stock', value: statistics.totalStockItems - statistics.lowStockItems },
    { name: 'Low Stock', value: statistics.lowStockItems }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-primary/10">
            <BarChart3 className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Business Reports</h1>
            <p className="text-muted-foreground mt-1">Analytics and insights for your business</p>
          </div>
        </div>
        <Button onClick={() => handleExport('summary')} variant="outline">
          <Download className="h-4 w-4 mr-2" />
          Export Summary
        </Button>
      </div>

      {/* Date Range Selector */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium">Date Range:</span>
            <div className="flex gap-2">
              {[7, 30, 60, 90].map(days => (
                <Button
                  key={days}
                  variant={dateRange === days ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleDateRangeChange(days)}
                >
                  {days} days
                </Button>
              ))}
            </div>
            <div className="ml-auto text-sm text-muted-foreground">
              {format(startDate, 'MMM dd, yyyy')} - {format(endDate, 'MMM dd, yyyy')}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <Package className="h-8 w-8 text-blue-600" />
              <TrendingUp className="h-4 w-4 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold">{statistics.totalOrders}</h3>
            <p className="text-sm text-muted-foreground">Total Orders</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <Users className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold">{statistics.totalCustomers}</h3>
            <p className="text-sm text-muted-foreground">Total Customers</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <CalendarIcon className="h-8 w-8 text-purple-600" />
            </div>
            <h3 className="text-2xl font-bold">{statistics.avgAttendance}</h3>
            <p className="text-sm text-muted-foreground">Avg Daily Attendance</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="h-8 w-8 text-orange-600" />
            </div>
            <h3 className="text-2xl font-bold">
              {statistics.completedTodos}/{statistics.totalTodos}
            </h3>
            <p className="text-sm text-muted-foreground">Tasks Completed</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Section */}
      <Tabs defaultValue="orders" className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="orders">Orders</TabsTrigger>
          <TabsTrigger value="attendance">Attendance</TabsTrigger>
          <TabsTrigger value="inventory">Inventory</TabsTrigger>
          <TabsTrigger value="tasks">Tasks</TabsTrigger>
          <TabsTrigger value="activity">
            <Activity className="h-4 w-4 mr-2" />
            Activity Log
          </TabsTrigger>
        </TabsList>

        {/* Orders Tab */}
        <TabsContent value="orders" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Orders by Status</CardTitle>
                <CardDescription>Distribution of order statuses</CardDescription>
              </CardHeader>
              <CardContent>
                {orderStatusData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={orderStatusData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {orderStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    No order data available
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
                <CardDescription>Order statistics breakdown</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(statistics.ordersByStatus).map(([status, count]) => (
                    <div key={status} className="flex items-center justify-between py-2 border-b last:border-b-0">
                      <span className="font-medium">{status}</span>
                      <Badge variant="outline">{count} orders</Badge>
                    </div>
                  ))}
                  {Object.keys(statistics.ordersByStatus).length === 0 && (
                    <p className="text-center text-muted-foreground py-4">No orders yet</p>
                  )}
                </div>
                <Button 
                  variant="outline" 
                  className="w-full mt-4"
                  onClick={() => handleExport('orders')}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export Orders
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Attendance Tab */}
        <TabsContent value="attendance" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Attendance Overview</CardTitle>
              <CardDescription>Employee attendance statistics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-6 border rounded-lg">
                  <p className="text-3xl font-bold">{statistics.totalEmployees}</p>
                  <p className="text-sm text-muted-foreground mt-2">Total Employees</p>
                </div>
                <div className="text-center p-6 border rounded-lg">
                  <p className="text-3xl font-bold text-green-600">{statistics.avgAttendance}</p>
                  <p className="text-sm text-muted-foreground mt-2">Avg Present/Day</p>
                </div>
                <div className="text-center p-6 border rounded-lg">
                  <p className="text-3xl font-bold">{reportData.attendance.length}</p>
                  <p className="text-sm text-muted-foreground mt-2">Total Records</p>
                </div>
              </div>
              <Button 
                variant="outline" 
                className="w-full mt-6"
                onClick={() => handleExport('attendance')}
              >
                <Download className="h-4 w-4 mr-2" />
                Export Attendance
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Inventory Tab */}
        <TabsContent value="inventory" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Stock Status</CardTitle>
                <CardDescription>Inventory health overview</CardDescription>
              </CardHeader>
              <CardContent>
                {stockStatusData.some(d => d.value > 0) ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={stockStatusData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value }) => `${name}: ${value}`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        <Cell fill="#10b981" />
                        <Cell fill="#f59e0b" />
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    No inventory data available
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Inventory Summary</CardTitle>
                <CardDescription>Stock statistics</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="font-medium">Total Items</span>
                    <Badge variant="outline">{statistics.totalStockItems}</Badge>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="font-medium">In Stock</span>
                    <Badge variant="default">{statistics.totalStockItems - statistics.lowStockItems}</Badge>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="font-medium">Low Stock</span>
                    <Badge variant="destructive">{statistics.lowStockItems}</Badge>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  className="w-full mt-6"
                  onClick={() => handleExport('inventory')}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export Inventory
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tasks Tab */}
        <TabsContent value="tasks" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Task Completion</CardTitle>
                <CardDescription>Todo completion rate</CardDescription>
              </CardHeader>
              <CardContent>
                {todoCompletionData.some(d => d.value > 0) ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={todoCompletionData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        <Cell fill="#10b981" />
                        <Cell fill="#f59e0b" />
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    No task data available
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Task Statistics</CardTitle>
                <CardDescription>Task management overview</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="font-medium">Total Tasks</span>
                    <Badge variant="outline">{statistics.totalTodos}</Badge>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="font-medium">Completed</span>
                    <Badge variant="default">{statistics.completedTodos}</Badge>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="font-medium">Pending</span>
                    <Badge variant="secondary">{statistics.totalTodos - statistics.completedTodos}</Badge>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="font-medium">Completion Rate</span>
                    <Badge variant="outline">
                      {statistics.totalTodos > 0 
                        ? `${((statistics.completedTodos / statistics.totalTodos) * 100).toFixed(0)}%`
                        : '0%'
                      }
                    </Badge>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  className="w-full mt-6"
                  onClick={() => handleExport('todos')}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export Tasks
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Activity Log Tab */}
        <TabsContent value="activity" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Activity Log</CardTitle>
                  <CardDescription>All changes made to the system with username, timestamp, and details</CardDescription>
                </div>
                <Button onClick={loadActivityLogs}>
                  <Activity className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="flex gap-4 mb-6">
                <div className="flex-1">
                  <label className="text-sm font-medium mb-2 block">Filter by Entity Type</label>
                  <select
                    value={activityFilter.entity_type}
                    onChange={(e) => {
                      setActivityFilter({ ...activityFilter, entity_type: e.target.value });
                      setTimeout(loadActivityLogs, 100);
                    }}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="">All Types</option>
                    <option value="Customer">Customer</option>
                    <option value="Address">Address</option>
                    <option value="Order">Order</option>
                    <option value="CAD Order">CAD Order</option>
                    <option value="Attendance">Attendance</option>
                    <option value="Stock">Stock</option>
                    <option value="Todo">Todo</option>
                    <option value="User">User</option>
                  </select>
                </div>
              </div>

              {/* Activity Log Table */}
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[150px]">Timestamp</TableHead>
                      <TableHead className="w-[100px]">User</TableHead>
                      <TableHead className="w-[80px]">Action</TableHead>
                      <TableHead className="w-[100px]">Entity Type</TableHead>
                      <TableHead>Entity Name</TableHead>
                      <TableHead>Changes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activityLogs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          <Activity className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p>No activity logs found for the selected period</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      activityLogs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell className="text-sm">
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3 text-muted-foreground" />
                              {format(new Date(log.timestamp), 'MMM dd, yyyy HH:mm')}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{log.username}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant={
                                log.action === 'Created' ? 'default' : 
                                log.action === 'Updated' ? 'secondary' : 
                                'destructive'
                              }
                            >
                              {log.action}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm font-medium">{log.entity_type}</span>
                          </TableCell>
                          <TableCell>
                            <span className="font-medium">{log.entity_name || '-'}</span>
                          </TableCell>
                          <TableCell>
                            <div className="text-xs text-muted-foreground max-w-xs truncate">
                              {log.changes && Object.keys(log.changes).length > 0 ? (
                                <span>{JSON.stringify(log.changes)}</span>
                              ) : (
                                '-'
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">{activityLogs.length}</div>
                    <p className="text-sm text-muted-foreground">Total Activities</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-green-600">
                      {activityLogs.filter(l => l.action === 'Created').length}
                    </div>
                    <p className="text-sm text-muted-foreground">Created</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-blue-600">
                      {activityLogs.filter(l => l.action === 'Updated').length}
                    </div>
                    <p className="text-sm text-muted-foreground">Updated</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-red-600">
                      {activityLogs.filter(l => l.action === 'Deleted').length}
                    </div>
                    <p className="text-sm text-muted-foreground">Deleted</p>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Reports;